import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiActivity, FiRefreshCw, FiPlay } from 'react-icons/fi';
import SessionCard from '../components/sessions/SessionCard';
import { getApiUrl } from '../utils/api';

interface Session {
  id: string;
  notebook_id: string;
  instance_id: string;
  instance_type: string;
  provider: string;
  region: string;
  proxy_status: string;
  created_at: string | null;
  last_accessed: string | null;
  cost_per_hour: number;
  jupyter_url: string | null;
}

export default function SessionsPage() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stoppingSessions, setStoppingSessions] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchSessions();
  }, [navigate]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/notebooks/sessions/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSessions();
  };

  const handleStopSession = async (sessionId: string) => {
    setStoppingSessions(prev => new Set(prev).add(sessionId));
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/notebooks/session/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSessions(sessions.filter(s => s.id !== sessionId));
      } else {
        alert('Failed to stop session');
      }
    } catch (error) {
      console.error('Error stopping session:', error);
      alert('Failed to stop session');
    } finally {
      setStoppingSessions(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const handleOpenNotebook = (jupyterUrl: string) => {
    window.open(jupyterUrl, '_blank');
  };

  const getTotalCost = () => {
    let total = 0;
    const now = new Date();
    for (const session of sessions) {
      if (session.created_at && session.cost_per_hour) {
        const start = new Date(session.created_at);
        const hours = (now.getTime() - start.getTime()) / 3600000;
        total += hours * session.cost_per_hour;
      }
    }
    return total.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white">Active Sessions</h1>
            <p className="text-gray-400 mt-1">
              {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
              {sessions.length > 0 && ` • $${getTotalCost()} total cost this session`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-gray-300 font-medium rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/dashboard/notebooks')}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              <FiPlay className="w-4 h-4" />
              Launch Notebook
            </button>
          </div>
        </div>

        {/* Sessions Grid */}
        {sessions.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiActivity className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-white font-medium mb-2">No active sessions</h3>
            <p className="text-gray-400 text-sm mb-6">
              Launch a notebook to start a new session
            </p>
            <button
              onClick={() => navigate('/dashboard/notebooks')}
              className="px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              Launch Notebook
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onStop={handleStopSession}
                onOpen={handleOpenNotebook}
                stopping={stoppingSessions.has(session.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
